import { Pagination as MuiPagination, PaginationRenderItemParams, TablePaginationProps } from "@mui/material";
import { SenecFunctionalIconButton } from "src/components/button/senec-functional-button/SenecFunctionalIconButton";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "src/foundations/icons";
import { GridPagination, GridSlotProps, PaginationPropsOverrides } from "@mui/x-data-grid";
import React from "react";

declare module "@mui/x-data-grid" {
    interface PaginationPropsOverrides {
        isLoading: boolean;
    }
}

function Pagination({
    page,
    count,
    rowsPerPage,
    onPageChange,
    isLoading,
}: TablePaginationProps & {
    page: number;
    count: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    isLoading?: boolean;
}) {
    const total_pages = Math.ceil(count / rowsPerPage);
    const currentPage = page + 1;

    const isDisabled = (item: PaginationRenderItemParams) => {
        if (isLoading) return true;
        if (item.type === "previous") return item.disabled;
        // item.disabled is not working on server-side pagination, therefore we have to determine the last page
        if (item.type === "next") return currentPage >= total_pages;
        return false;
    };

    return (
        <MuiPagination
            count={count}
            page={currentPage}
            onChange={(event, newPage) => {
                if (!isLoading) {
                    onPageChange(event as React.MouseEvent<HTMLButtonElement> | null, newPage - 1);
                }
            }}
            renderItem={(item) => {
                if (item.type === "previous")
                    return (
                        <SenecFunctionalIconButton onClick={item.onClick} level="secondary" disabled={isDisabled(item)}>
                            <ChevronLeftIcon />
                        </SenecFunctionalIconButton>
                    );

                if (item.type === "next") {
                    return (
                        <SenecFunctionalIconButton onClick={item.onClick} level="secondary" disabled={isDisabled(item)}>
                            <ChevronRightIcon />
                        </SenecFunctionalIconButton>
                    );
                }
            }}
        />
    );
}

function CustomSelectIcon() {
    return <ChevronDownIcon sx={{ position: "absolute", right: "8px", pointerEvents: "none" }} />;
}

export function SenecDataGridPagination({ isLoading, ...props }: GridSlotProps["pagination"] & PaginationPropsOverrides) {
    return (
        <GridPagination
            ActionsComponent={(props) => (
                <Pagination
                    page={props.page}
                    rowsPerPage={props.rowsPerPage}
                    count={props.count}
                    onPageChange={props.onPageChange}
                    isLoading={isLoading}
                />
            )}
            {...props}
            slotProps={{
                select: {
                    IconComponent: CustomSelectIcon,
                },
            }}
            sx={{
                "& .MuiPagination-ul": { flexWrap: "noWrap", gap: "12px", marginLeft: "12px" },
                "& .MuiPagination-ul li:empty": { display: "none" },
                "& .MuiPagination-ul .MuiIconButton-root": { width: "40px", height: "40px" },
            }}
        />
    );
}
